import React, { useRef, useState, useEffect } from 'react';
import { amountInWords } from '../lib/amountInWords';

const ORANGE = '#F97316';
const INK    = '#0F172A';
const BODY   = '#475569';
const MUTED  = '#94A3B8';
const LINE   = '#E2E8F0';
const SOFT   = '#F8FAFC';

const MONTHS = ['January','February','March','April','May','June','July','August',
                'September','October','November','December'];

function fmtDate(d) {
  if (!d) return '';
  const p = d.split('-');
  if (p.length !== 3) return d;
  return `${parseInt(p[2])} ${MONTHS[parseInt(p[1]) - 1]} ${p[0]}`;
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return 'Rs. ' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtTerms(term) {
  const map = { net30: 'Net 30', net45: 'Net 45', net60: 'Net 60', net90: 'Net 90' };
  return map[term] || 'Custom';
}

function HR({ dark } = {}) {
  return <div style={{ height: dark ? 1 : 0.75, background: dark ? INK : LINE, margin: dark ? '0' : '12px 0' }} />;
}

export default function InvoicePreview({ invoice }) {
  const {
    business = {}, customer = {}, lineItems = [],
    taxSummary = {}, number, date, dueDate, notes, paymentTerms, customPaymentTerms,
  } = invoice;

  const bd = business.bankDetails || {};

  const effRate = taxSummary.taxableAmount > 0
    ? Math.round((((taxSummary.igst || 0) + (taxSummary.cgst || 0) + (taxSummary.sgst || 0)) / taxSummary.taxableAmount) * 100)
    : 18;

  // zoom scales both visual and layout dimensions — wrapper height grows with content
  const A4_W = 794;
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setZoom(entry.contentRect.width / A4_W);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} style={{ width: '100%', overflow: 'hidden' }}>
    <div style={{
      width: A4_W,
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: 10,
      color: INK,
      zoom,
      // white background + a 1px slate line every A4-height to show page breaks
      background: `white repeating-linear-gradient(
        to bottom,
        transparent 0px,
        transparent 1122px,
        #CBD5E1 1122px,
        #CBD5E1 1123px
      )`,
    }}>
      {/* Orange top stripe */}
      <div style={{ height: 5, background: ORANGE }} />

      <div style={{ padding: '36px 40px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: INK, letterSpacing: -0.5, lineHeight: 1 }}>TAX INVOICE</div>
            <div style={{ fontSize: 8, color: MUTED, marginTop: 4, letterSpacing: 0.2 }}>powered by billjaldi.com</div>
            <div style={{ fontSize: 10, color: MUTED, marginTop: 5 }}># {number || 'INV-001'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {business.logo ? (
              <img src={business.logo} alt="logo" style={{ height: 42, maxWidth: 120, objectFit: 'contain', marginBottom: 6, display: 'block', marginLeft: 'auto' }} />
            ) : (
              <div style={{
                width: 42, height: 42, background: ORANGE, borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 6, marginLeft: 'auto',
                fontSize: 24, fontWeight: 900, color: 'white',
              }}>b</div>
            )}
            <div style={{ fontSize: 12, fontWeight: 800, color: INK }}>{business.name || 'Your Business'}</div>
            {business.address && <div style={{ fontSize: 9, color: BODY, marginTop: 2, lineHeight: 1.5 }}>{business.address}</div>}
            {business.gstin  && <div style={{ fontSize: 9, color: BODY, lineHeight: 1.5 }}>GSTIN: {business.gstin}</div>}
          </div>
        </div>

        <HR />

        {/* Dates */}
        <div style={{ display: 'flex', gap: 32, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 800, color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Date</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: INK }}>{fmtDate(date) || '—'}</div>
          </div>
          {dueDate && (
            <div>
              <div style={{ fontSize: 8, fontWeight: 800, color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Due</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: INK }}>{fmtDate(dueDate)}</div>
            </div>
          )}
          {paymentTerms && (paymentTerms !== 'custom' || customPaymentTerms) && (
            <div>
              <div style={{ fontSize: 8, fontWeight: 800, color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Terms</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: INK }}>{paymentTerms === 'custom' ? customPaymentTerms : fmtTerms(paymentTerms)}</div>
            </div>
          )}
        </div>

        <HR />

        {/* Bill To */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 8, fontWeight: 800, color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>Bill To</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: INK, marginBottom: 3 }}>{customer.name || '—'}</div>
          {customer.gstin    && <div style={{ fontSize: 9, color: BODY, lineHeight: 1.6 }}>GSTIN: {customer.gstin}</div>}
          {customer.phone    && <div style={{ fontSize: 9, color: BODY, lineHeight: 1.6 }}>Phone: {customer.phone}</div>}
          {customer.address  && <div style={{ fontSize: 9, color: BODY, lineHeight: 1.6 }}>{customer.address}</div>}
          {customer.stateName && <div style={{ fontSize: 9, color: BODY, lineHeight: 1.6 }}>{customer.stateName}{customer.email ? '  ·  ' + customer.email : ''}</div>}
        </div>

        <HR />

        {/* Line items table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
          <thead>
            <tr>
              {['Description', 'HSN', 'Qty', 'Rate', 'Amount'].map((h, i) => (
                <th key={h} style={{
                  fontSize: 8, fontWeight: 800, color: MUTED,
                  textTransform: 'uppercase', letterSpacing: 0.4,
                  textAlign: i >= 2 ? 'right' : 'left',
                  paddingBottom: 6,
                  width: i === 0 ? '40%' : i === 1 ? '16%' : i === 2 ? '10%' : i === 3 ? '18%' : '16%',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
        </table>
        <HR dark />
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '16%' }} />
          </colgroup>
          <tbody>
            {lineItems.map((li, i) => {
              const qty     = Number(li.qty || 0);
              const rate    = Number(li.rate || 0);
              const disc    = Number(li.discount || 0);
              const gstR    = Number(li.gstRate || 0);
              const taxable = qty * rate * (1 - disc / 100);
              const total   = taxable * (1 + gstR / 100);
              return (
                <tr key={li.id || i} style={{ borderBottom: `0.5px solid ${LINE}` }}>
                  <td style={{ fontSize: 10, color: INK, padding: '8px 0' }}>{li.description || ''}</td>
                  <td style={{ fontSize: 9, color: BODY, padding: '8px 0' }}>{li.hsn || ''}</td>
                  <td style={{ fontSize: 9, color: BODY, padding: '8px 0', textAlign: 'right' }}>{qty % 1 === 0 ? qty : qty.toFixed(2)}</td>
                  <td style={{ fontSize: 9, color: BODY, padding: '8px 0', textAlign: 'right' }}>{fmtMoney(rate)}</td>
                  <td style={{ fontSize: 10, fontWeight: 700, color: INK, padding: '8px 0', textAlign: 'right' }}>{fmtMoney(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <div style={{ width: 220 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ fontSize: 11, color: BODY }}>Subtotal</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: INK }}>{fmtMoney(taxSummary.subtotal)}</span>
            </div>
            {(taxSummary.totalDiscount || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ fontSize: 11, color: BODY }}>Discount</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: INK }}>-{fmtMoney(taxSummary.totalDiscount)}</span>
              </div>
            )}
            {taxSummary.isInterState ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ fontSize: 11, color: BODY }}>IGST @ {effRate}%</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: INK }}>{fmtMoney(taxSummary.igst)}</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ fontSize: 11, color: BODY }}>CGST @ {effRate / 2}%</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: INK }}>{fmtMoney(taxSummary.cgst)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span style={{ fontSize: 11, color: BODY }}>SGST @ {effRate / 2}%</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: INK }}>{fmtMoney(taxSummary.sgst)}</span>
                </div>
              </>
            )}
            <HR dark />
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: INK }}>TOTAL</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: ORANGE }}>{fmtMoney(taxSummary.grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* Amount in words */}
        <div style={{ background: SOFT, border: `0.5px solid ${LINE}`, borderRadius: 4, padding: 10, marginTop: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 8, fontWeight: 800, color: ORANGE, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Amount in Words</div>
          <div style={{ fontSize: 11, fontStyle: 'italic', color: INK }}>{amountInWords(taxSummary.grandTotal || 0)}</div>
        </div>

        <HR />

        {/* Notes + Bank + Signature */}
        {(notes || bd.bankName || bd.accountNo || bd.ifsc || bd.upi || business.sealSignature) && (
          <div style={{ display: 'flex', gap: 24 }}>
            {notes && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, fontWeight: 800, color: MUTED, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 10, color: BODY, lineHeight: 1.6 }}>{notes}</div>
              </div>
            )}
            {(bd.bankName || bd.accountNo || bd.ifsc || bd.upi) && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, fontWeight: 800, color: MUTED, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>Bank Details</div>
                {bd.bankName  && <div style={{ fontSize: 10, color: BODY, lineHeight: 1.6 }}>{bd.bankName}</div>}
                {bd.accountNo && <div style={{ fontSize: 10, color: BODY, lineHeight: 1.6 }}>Acc: {bd.accountNo}</div>}
                {bd.ifsc      && <div style={{ fontSize: 10, color: BODY, lineHeight: 1.6 }}>IFSC: {bd.ifsc}</div>}
                {bd.upi       && <div style={{ fontSize: 10, color: BODY, lineHeight: 1.6 }}>UPI: {bd.upi}</div>}
              </div>
            )}
            {business.sealSignature && (
              <div style={{ flex: 1, textAlign: 'right' }}>
                <div style={{ fontSize: 8, fontWeight: 800, color: MUTED, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>Authorised Signature</div>
                <img src={business.sealSignature} alt="Seal and signature" style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain', display: 'inline-block' }} />
                <div style={{ fontSize: 9, color: BODY, marginTop: 4 }}>{business.name || 'Your Business'}</div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
    </div>
  );
}
